import axiosConfig from './axiosConfig';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createListFetcher } from './listFetchers';
import { queryClient } from './queryClient';

/**
 * Get list of all vendors
 * @param {Object} queryParams Optional query parameters
 * @returns {Promise} Response with list of vendors
 */
export const getVendors = createListFetcher('vendors');

/**
 * Create a new vendor
 * @param {Object} vendorData Vendor data to create
 * @returns {Promise} Response with created vendor data
 */
export const createVendor = async (vendorData) => {
    try {
        const response = await axiosConfig.post('vendors', vendorData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response;
    } catch (error) {
        throw error || 'Failed to create vendor';
    }
};

/**
 * Get a vendor by ID (with related spareparts)
 * @param {string|number} vendorId Vendor ID
 * @returns {Promise} Response with vendor and related spareparts
 */
export const getVendorById = async (vendorId) => {
    try {
        const response = await axiosConfig.get(`vendors/${vendorId}`);
        return response;
    } catch (error) {
        throw error;
    }
};

export const useVendorDetails = (vendorId, options = {}) => useQuery({
    queryKey: ['vendorDetails', vendorId],
    queryFn: async () => {
        const response = await getVendorById(vendorId);
        return response.data;
    },
    enabled: !!vendorId,
    keepPreviousData: true,
    retry: 1,
    ...options,
});

/**
 * Update a vendor by ID (PUT)
 * @param {string|number} vendorId Vendor ID
 * @param {Object} vendorData Vendor data to update
 * @returns {Promise} Response with updated vendor data
 */
export const updateVendor = async (vendorId, vendorData) => {
    try {
        const response = await axiosConfig.put(`vendors/${vendorId}`, vendorData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response;
    } catch (error) {
        throw error || 'Failed to update vendor';
    }
};

/**
 * Partially update a vendor by ID (PATCH)
 * @param {string|number} vendorId Vendor ID
 * @param {Object} vendorData Vendor data to update
 * @returns {Promise} Response with updated vendor data
 */
export const patchVendor = async (vendorId, vendorData) => {
    try {
        const response = await axiosConfig.patch(`vendors/${vendorId}`, vendorData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response;
    } catch (error) {
        throw error || 'Failed to update vendor';
    }
};

/**
 * Delete a vendor by ID (soft delete)
 * @param {string|number} vendorId Vendor ID
 * @returns {Promise} Response from delete operation
 */
export const deleteVendor = async (vendorId) => {
    try {
        const response = await axiosConfig.delete(`vendors/${vendorId}`);
        return response;
    } catch (error) {
        throw error || 'Failed to delete vendor';
    }
};

/**
 * Get vendor categories
 * @returns {Promise} Response with vendor categories
 */
export const getVendorCategories = async () => {
    try {
        const response = await axiosConfig.get('vendors/categories');
        return response;
    } catch (error) {
        throw error;
    }
};

export const useVendorList = (options = {}) => useQuery({
    queryKey: ['vendors', 'list'],
    queryFn: () => getVendors({ per_page: 50 }).then(response => response.data?.data ?? []),
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    retry: 1,
    ...options,
});

export const useCreateVendor = (options = {}) => {
    return useMutation({
        mutationFn: createVendor,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendors'] }),
        ...options,
    });
};

export const useUpdateVendor = (options = {}) => {
    return useMutation({
        mutationFn: ({ id, vendorData }) => updateVendor(id, vendorData),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendors'] }),
        ...options,
    });
};

export const useDeleteVendor = (options = {}) => {
    return useMutation({
        mutationFn: deleteVendor,
        onSuccess: (_, id) => queryClient.invalidateQueries({ queryKey: ['vendors'] }),
        ...options,
    });
};
